import React, {
  FC,
  ReactNode,
  createContext,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useLicenses, useLogger } from '~Hooks';
import { useStorage } from '~Components/Storage';
import { CardIdentity } from '~reducers';
import ActiveLoyaltyProvider from './ActiveLoyaltyProvider';

/** What a card prompt ended with - the cart's loyalty modal shows one screen per outcome. */
export type ExpressLoyaltyIdentifyResult =
  /** A member was found and attached to the cart. */
  | { status: 'member'; memberName: string | null }
  /** The card was read but belongs to no member - it is not held. */
  | { status: 'unknown' }
  /** No card came back - our 60s timeout, the connection to the terminal giving up, or an error. */
  | { status: 'timeout' }
  /** Deliberately stopped (start over, closed, newer prompt), or loyalty not available here. */
  | { status: 'cancelled' };

/**
 * Loyalty on the kiosk: the shopper opens "Member?" in the cart and taps their
 * card on the terminal; the member (and their discount) lands on the cart.
 * The object never changes identity, so a component that only triggers it
 * never re-renders because of it.
 */
export interface ExpressLoyaltyActions {
  /** Asks the terminal for a card and attaches the member to the cart. Resolves with the outcome. */
  identify: () => Promise<ExpressLoyaltyIdentifyResult>;
  /** Hands an unused card back to the terminal - start over, idle timeout, leaving without paying. */
  release: () => Promise<void>;
  /**
   * Called right before the payment is requested. Aborts a prompt the shopper
   * never answered and returns the held card (with its acquisition reference)
   * for the payment to charge, or null when there is none.
   */
  prepareForPayment: () => Promise<CardIdentity | null>;
}

/** Where the active engine registers itself; null means every call is a no-op. */
export type ExpressLoyaltyEngineRef = React.MutableRefObject<ExpressLoyaltyActions | null>;

const ExpressLoyaltyContext = createContext<ExpressLoyaltyActions | undefined>(undefined);

/** True while the terminal is asking for a loyalty card - its own context so the actions stay stable. */
const ExpressLoyaltyIdentifyingContext = createContext<boolean>(false);

interface Props {
  children: ReactNode;
}

/**
 * The gate. The context value is one stable object that delegates to whatever
 * engine is registered - nothing while the venue lacks Loyalty Pro or the
 * device has no Adyen terminal. The engine is a childless sibling, so turning
 * it on or off never changes the shape of the tree under it.
 */
export const ExpressLoyaltyProvider: FC<Props> = ({ children }) => {
  const logger = useLogger('epos-loyalty');
  const licenses = useLicenses();
  const [storage] = useStorage();
  const terminalId = storage.adyenTerminals[0]?.POIID;
  const enabled = licenses.hasLoyaltyPro && Boolean(terminalId);

  const engineRef: ExpressLoyaltyEngineRef = useRef(null);

  /** Prompts in flight - a count, since a retry can start before the old prompt has settled. */
  const [identifyCount, setIdentifyCount] = useState(0);

  useEffect(() => {
    logger.info('EPOS loyalty gate evaluated', {
      enabled,
      hasLoyaltyPro: licenses.hasLoyaltyPro,
      hasTerminal: Boolean(terminalId),
    });
  }, [enabled]);

  const actions = useMemo<ExpressLoyaltyActions>(
    () => ({
      identify: async () => {
        setIdentifyCount((count) => count + 1);
        try {
          return (await engineRef.current?.identify()) ?? { status: 'cancelled' };
        } finally {
          setIdentifyCount((count) => count - 1);
        }
      },
      release: async () => engineRef.current?.release(),
      prepareForPayment: async () => engineRef.current?.prepareForPayment() ?? null,
    }),
    [],
  );

  return (
    <ExpressLoyaltyContext.Provider value={actions}>
      <ExpressLoyaltyIdentifyingContext.Provider value={identifyCount > 0}>
        {enabled && terminalId && (
          <ActiveLoyaltyProvider terminalId={terminalId} engineRef={engineRef} />
        )}
        {children}
      </ExpressLoyaltyIdentifyingContext.Provider>
    </ExpressLoyaltyContext.Provider>
  );
};

/** Stable actions - safe in dependency arrays and never a reason to re-render. */
export const useExpressLoyalty = (): ExpressLoyaltyActions => {
  const context = useContext(ExpressLoyaltyContext);
  if (!context) {
    throw new Error('useExpressLoyalty must be used within an ExpressLoyaltyProvider');
  }
  return context;
};

/** True while the shopper is being asked to tap their loyalty card. Re-renders on every change. */
export const useExpressLoyaltyIdentifying = (): boolean =>
  useContext(ExpressLoyaltyIdentifyingContext);

export default ExpressLoyaltyProvider;
